"use client";

import {
  type ReactNode,
  type SyntheticEvent,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import {
  FiCamera,
  FiChevronLeft,
  FiChevronRight,
  FiCopy,
  FiEdit2,
  FiExternalLink,
  FiFileText,
  FiGitPullRequest,
  FiImage,
  FiMinusCircle,
  FiPlay,
  FiShare2,
  FiTrash2,
  FiUser,
  FiUsers,
  FiZap,
  FiZoomIn,
  FiZoomOut,
} from "react-icons/fi";
import { LuLoader2 } from "react-icons/lu";
import { useControls } from "react-zoom-pan-pinch";

import dynamic from "next/dynamic";
import Image from "next/image";
import Link from "next/link";
import { useParams } from "next/navigation";

import { ImageTagBadge } from "@/components/scribe/image-tag-badge";
import { DownloadMenuItem } from "@/components/scribe/save-to-photos";
import { Button, Input } from "@/components/ui";
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  DropdownSettingsButton,
} from "@/components/ui/dropdown";

import { useUser } from "@/contexts/user-provider";

import { useIntersectionObserver } from "@/hooks/use-intersection-observer";

import { formatName } from "@/lib/format-data";
import { isImageUrl, isVideoUrl } from "@/lib/image-compression";
import {
  PERMISSIONS,
  galleryPhotoActions,
  hasPermission,
} from "@/lib/permissions";
import {
  TRANSPARENT_CHECKER,
  isTransparentStudioRender,
} from "@/lib/photo-studio/studio-photo";
import { reportScribeAttachmentLoaded } from "@/lib/scribe-attachment-analytics";
import { cx } from "@/lib/tailwind-utils";

import { Annotation } from "@/types/annotation";
import { NoteAttachment, NoteAttachmentTag } from "@/types/notes";

import { AnnotationOverlay } from "./annotation-overlay";
import { downloadFromSignedUrl, getSignedDownloadUrl } from "@/api/auth/gcs";

// Dynamically import ImageAnnotator for read-only preview to avoid SSR issues
const ReadOnlyAnnotator = dynamic(() => import("./image-annotator"), {
  ssr: false,
});

export const Controls = ({ className }: { className?: string }) => {
  const { zoomIn, zoomOut } = useControls();

  return (
    <div
      className={cx("absolute right-2 top-2 z-10 flex gap-2 p-2", className)}
    >
      <Button
        variant="secondary"
        className="h-8 w-8 rounded-full border-white/40 bg-background/60 p-0"
        onClick={() => zoomIn()}
        icon={<FiZoomIn />}
      />
      <Button
        variant="secondary"
        className="h-8 w-8 rounded-full border-white/40 bg-background/60 p-0"
        onClick={() => zoomOut()}
        icon={<FiZoomOut />}
      />
    </div>
  );
};

type ResolvedAttachment = {
  id: string;
  url: string;
  thumbnailUrl: string;
  /** True when `thumbnailUrl` resolves to the offline thumbnail (a compressed
   * copy). False when it falls back to the full-resolution `signedUrl` because
   * the thumbnail-generation job hadn't produced one. Drives load-time
   * analytics — see `reportScribeAttachmentLoaded`. */
  thumbnailIsCompressed: boolean;
  name: string;
  annotations: Annotation[];
  tags: NoteAttachmentTag[];
  isPlaceholder: boolean;
  attachmentId: string;
  file?: File;
  gcsPath: string;
  note?: NoteAttachment["note"];
  patient?: NoteAttachment["patient"];
  noteId?: string | null;
  patientId?: string;
  isVideo: boolean;
};

const resolveAttachment = ({
  attachment,
  shouldShowThumbnail,
  noteId,
  patientId,
}: {
  attachment: NoteAttachment;
  shouldShowThumbnail: boolean;
  noteId?: string | null;
  patientId?: string;
}): ResolvedAttachment => {
  const url = attachment.signedUrl || attachment.url || "";
  const hasCompressedThumb = !!(
    attachment.thumbnailSignedUrl || attachment.thumbnailUrl
  );
  const thumbnailUrl = shouldShowThumbnail
    ? attachment.thumbnailSignedUrl ||
      attachment.thumbnailUrl ||
      attachment.signedUrl ||
      attachment.url ||
      ""
    : url;

  const gcsPath = attachment.gcsPath || "";
  const isVideo =
    (attachment.file ? attachment.file.type.startsWith("video/") : false) ||
    isVideoUrl(gcsPath) ||
    isVideoUrl(attachment.originalFilename || "");

  return {
    id: attachment.id,
    url,
    thumbnailUrl,
    thumbnailIsCompressed: shouldShowThumbnail && hasCompressedThumb,
    name: attachment.name || attachment.originalFilename || "",
    annotations: (attachment.annotations as Annotation[] | undefined) || [],
    tags: attachment.tags || [],
    isPlaceholder: attachment.isPlaceholder === true,
    attachmentId: attachment.attachmentId || attachment.id,
    file: attachment.file,
    gcsPath,
    note: attachment.note,
    patient: attachment.patient,
    noteId: attachment.note?.id ?? noteId ?? attachment.noteId ?? null,
    patientId: attachment.patient?.id ?? patientId,
    isVideo,
  };
};

// Lazy-loaded image component using shared Intersection Observer
const LazyImage = ({
  src,
  fallbackSrc,
  alt,
  width,
  height,
  className,
  onLoad,
  onError,
}: {
  src: string;
  /** Full-resolution URL to render if `src` (a thumbnail) fails to load. A
   * thumbnail can 404 when its offline generation job hasn't run yet. The
   * fallback is served through the Next image optimizer (`unoptimized={false}`)
   * so the browser downloads a small downscaled copy — decoding the multi-MB
   * original instead would OOM older iPads, the very thing thumbnails avoid. */
  fallbackSrc?: string;
  alt: string;
  width: number;
  height: number;
  className?: string;
  /** Fires once the rendered image has loaded. `usedFallback` reports whether
   * the `fallbackSrc` (full image) was shown rather than the primary thumbnail,
   * and `aspectRatio` is the natural width/height of whatever actually
   * rendered — so callers can report load analytics and size overlays against
   * the real displayed image (which matters when a thumbnail 404s and the full
   * image, with a different aspect ratio, takes its place). */
  onLoad?: (info: { usedFallback: boolean; aspectRatio?: number }) => void;
  onError?: () => void;
}) => {
  const [isInView, setIsInView] = useState(false);
  const [hasError, setHasError] = useState(false);
  const [primaryFailed, setPrimaryFailed] = useState(false);
  const [loadedSrc, setLoadedSrc] = useState(src);
  const [loadedFallbackSrc, setLoadedFallbackSrc] = useState(fallbackSrc);

  // Reset error/fallback state when either source URL changes (e.g. a cache
  // refetch hands us a fresh signed URL — the thumbnail and the full image can
  // re-sign independently) — adjust state during render rather than in an
  // effect.
  if (loadedSrc !== src || loadedFallbackSrc !== fallbackSrc) {
    setLoadedSrc(src);
    setLoadedFallbackSrc(fallbackSrc);
    setHasError(false);
    setPrimaryFailed(false);
  }

  // Use shared IntersectionObserver hook - single observer for all images
  const imgRef = useIntersectionObserver(
    () => {
      setIsInView(true);
    },
    {
      // Start loading when image is 200px away from viewport
      rootMargin: "200px",
    },
  );

  const canFallBack = !!fallbackSrc && fallbackSrc !== src;
  const usingFallback = primaryFailed && canFallBack;
  const displaySrc = usingFallback ? fallbackSrc : src;

  const handleError = () => {
    // Fall back to the full image once before showing the error state.
    if (!primaryFailed && canFallBack) {
      setPrimaryFailed(true);
      return;
    }
    setHasError(true);
    onError?.();
  };

  const handleLoad = (event: SyntheticEvent<HTMLImageElement>) => {
    const img = event.currentTarget;
    const aspectRatio =
      img.naturalWidth > 0 && img.naturalHeight > 0
        ? img.naturalWidth / img.naturalHeight
        : undefined;
    onLoad?.({ usedFallback: usingFallback, aspectRatio });
  };

  return (
    <div
      ref={imgRef}
      className={className}
      style={{ width: "100%", height: "100%" }}
    >
      {hasError ? (
        <div className="flex h-full flex-col items-center justify-center p-2 bg-background-secondary">
          <FiImage className="mb-2 h-6 w-6 text-foreground-muted" />
          <p className="text-center text-caption text-foreground-secondary">
            Image not available
          </p>
        </div>
      ) : isInView ? (
        <Image
          src={displaySrc}
          alt={alt}
          width={width}
          height={height}
          className="h-full w-full object-cover"
          // A Studio photo on the Transparent backdrop sits on a checkerboard,
          // as in the camera and the viewer, so it doesn't read as white.
          style={
            isTransparentStudioRender(displaySrc)
              ? { background: TRANSPARENT_CHECKER }
              : undefined
          }
          // Thumbnails are already small, so skip the optimizer for them. The
          // fallback is the full-resolution original — route it THROUGH the
          // optimizer so the iPad only decodes a downscaled copy.
          unoptimized={!usingFallback}
          onError={handleError}
          onLoad={handleLoad}
        />
      ) : (
        <div className="flex h-full items-center justify-center bg-background-secondary">
          <LuLoader2 className="h-4 w-4 animate-spin text-foreground-muted" />
        </div>
      )}
    </div>
  );
};

const FaceSilhouette = ({ className }: { className?: string }) => (
  <svg
    viewBox="0 0 64 64"
    fill="none"
    stroke="currentColor"
    strokeWidth={1.5}
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
    className={className}
  >
    <path d="M16 28 C16 16, 48 16, 48 28" />
    <path d="M16 28 C16 46, 24 56, 32 56 C40 56, 48 46, 48 28" />
    <circle cx="25" cy="33" r="1.2" fill="currentColor" stroke="none" />
    <circle cx="39" cy="33" r="1.2" fill="currentColor" stroke="none" />
    <path d="M28 44 C30 46, 34 46, 36 44" />
  </svg>
);

const isLocalFileUrl = (url?: string) => !!url?.startsWith("blob:");

const AttachmentMedia = ({
  resolved,
  attachment,
  isUploading,
  converting,
  isOptimisticEntry,
  hasValidImageUrl,
  onCameraOpen,
}: {
  resolved: ResolvedAttachment;
  attachment: NoteAttachment;
  isUploading: boolean;
  converting: boolean;
  isOptimisticEntry: boolean;
  hasValidImageUrl: boolean;
  onCameraOpen?: () => void;
}) => {
  // Aspect ratio of the image actually on screen — captured from the loaded
  // <Image> so the annotation overlay stays aligned even when a thumbnail 404s
  // and the full image (with a different aspect ratio) is shown instead.
  const [displayedAspectRatio, setDisplayedAspectRatio] = useState<
    number | undefined
  >(undefined);
  // Only a local file is still uploading. An optimistic entry already in
  // storage (a template image being copied) shows as a normal image.
  const isUploadingFile = isOptimisticEntry && isLocalFileUrl(resolved.url);

  if (resolved.isPlaceholder && !hasValidImageUrl) {
    return (
      <div className="relative flex h-full flex-col items-center justify-center p-2">
        {!isUploading && (
          <FaceSilhouette className="pointer-events-none absolute inset-0 m-auto h-4/5 w-4/5 text-foreground-muted opacity-20" />
        )}
        {onCameraOpen && !isUploading && (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onCameraOpen();
            }}
            aria-label="Take picture"
            title="Take picture"
            className="absolute -left-1 -top-1 z-10 flex h-6 w-6 items-center justify-center rounded-full bg-black/50 text-white transition-colors hover:bg-black/70"
          >
            <FiCamera className="h-3 w-3" />
          </button>
        )}
        <div className="relative z-[1] flex flex-col items-center">
          {isUploading ? (
            <>
              <LuLoader2 className="mb-2 h-6 w-6 animate-spin text-foreground-muted" />
              <p className="text-caption text-foreground-secondary">
                Uploading...
              </p>
            </>
          ) : (
            <p className="text-center text-caption text-foreground-secondary">
              Click to upload
            </p>
          )}
        </div>
      </div>
    );
  }

  if (resolved.isVideo) {
    return (
      <>
        <video
          src={resolved.thumbnailUrl ?? ""}
          className="h-full w-full object-cover"
          muted
          playsInline
          preload="metadata"
          onLoadedMetadata={() => reportScribeAttachmentLoaded(false)}
        />
        {/* Play icon overlay */}
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
          <div className="rounded-full bg-black/50 p-2">
            <FiPlay className="h-5 w-5 text-white" />
          </div>
        </div>

        {isUploadingFile && (
          <div className="absolute inset-0 flex flex-col items-center justify-center bg-black/40">
            <LuLoader2 className="h-6 w-6 animate-spin text-white" />
            <span className="mt-1 text-caption text-white">Uploading...</span>
          </div>
        )}
      </>
    );
  }

  return (
    <>
      <LazyImage
        src={resolved.thumbnailUrl}
        fallbackSrc={resolved.url}
        alt="Attachment"
        width={200}
        height={200}
        className="h-full w-full"
        onLoad={({ usedFallback, aspectRatio }) => {
          if (aspectRatio) setDisplayedAspectRatio(aspectRatio);
          reportScribeAttachmentLoaded(
            resolved.thumbnailIsCompressed && !usedFallback,
          );
        }}
      />

      <AnnotationOverlay
        annotations={resolved.annotations}
        imageFile={attachment.file}
        imageUrl={resolved.thumbnailUrl}
        aspectRatio={displayedAspectRatio}
        containerSize={200}
      />

      {converting && (
        <div className="absolute inset-0 flex items-center justify-center bg-black/40">
          <LuLoader2 className="h-6 w-6 animate-spin text-white" />
        </div>
      )}

      {isUploadingFile && (
        <div className="absolute inset-0 flex flex-col items-center justify-center bg-black/40">
          <LuLoader2 className="h-6 w-6 animate-spin text-white" />
          <span className="mt-1 text-caption text-white">Uploading...</span>
        </div>
      )}
    </>
  );
};

interface AttachmentGalleryDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  attachments: ResolvedAttachment[];
  currentIndex: number;
  onIndexChange: (index: number) => void;
  onEditAnnotations: (attachment: ResolvedAttachment) => void;
  onLinkToNote?: (attachment: ResolvedAttachment) => void;
  onUnlinkFromNote?: (attachment: ResolvedAttachment) => void;
  onChangePatient?: (attachment: ResolvedAttachment) => void;
  onDuplicate?: (attachment: ResolvedAttachment) => void;
  onRename?: (attachment: ResolvedAttachment) => void;
  onCompare?: (attachment: ResolvedAttachment) => void;
  onRemove?: (attachment: ResolvedAttachment) => void;
  onSkinAnalysis?: (attachment: ResolvedAttachment) => void;
  onSetAsProfilePhoto?: (attachment: ResolvedAttachment) => void;
  isDuplicating?: boolean;
  isAnyMutating?: boolean;
  /** When true, hides "Edit Annotations" from the dropdown (e.g. in playground where click-to-annotate is used) */
  hideEditAnnotations?: boolean;
  /** Shown instead of the current photo (e.g. its Studio version). */
  imageUrlOverride?: string | null;
  /** The override is a Studio photo on a transparent backdrop. */
  imageTransparent?: boolean;
  /** Extra controls in the header, next to the menu (e.g. Studio controls). */
  headerExtras?: ReactNode;
  /** Saves the current photo with no Studio backdrop, for everyone. */
  onShowAsTaken?: () => void;
}

const AttachmentGalleryDialog = ({
  open,
  onOpenChange,
  attachments,
  currentIndex,
  onIndexChange,
  onEditAnnotations,
  onLinkToNote,
  onUnlinkFromNote,
  onChangePatient,
  onDuplicate,
  onRename,
  onCompare,
  onRemove,
  onSkinAnalysis,
  onSetAsProfilePhoto,
  isDuplicating,
  isAnyMutating,
  hideEditAnnotations,
  imageUrlOverride,
  imageTransparent = false,
  headerExtras,
  onShowAsTaken,
}: AttachmentGalleryDialogProps) => {
  const current = attachments[currentIndex];
  const { currentUser } = useUser();
  const canDownload = hasPermission(
    currentUser,
    PERMISSIONS.CLINICAL_GALLERY_DOWNLOAD,
  );
  const photoActions = galleryPhotoActions(currentUser);
  const touchStartRef = useRef<{ x: number; y: number } | null>(null);

  const goNext = useCallback(() => {
    if (currentIndex < attachments.length - 1) onIndexChange(currentIndex + 1);
  }, [currentIndex, attachments.length, onIndexChange]);

  const goPrev = useCallback(() => {
    if (currentIndex > 0) onIndexChange(currentIndex - 1);
  }, [currentIndex, onIndexChange]);

  // Keyboard navigation
  useEffect(() => {
    if (!open) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === "ArrowRight") goNext();
      else if (e.key === "ArrowLeft") goPrev();
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [open, goNext, goPrev]);

  if (!current) return null;

  const handleDownload = async () => {
    const gcsPath = current.gcsPath;
    if (!gcsPath) return;
    try {
      const filename = current.name || `photo-${current.id.slice(0, 8)}.jpg`;
      const result = await getSignedDownloadUrl(gcsPath, filename);
      if (!result?.signedUrl) return;
      await downloadFromSignedUrl(result.signedUrl, filename);
    } catch (error) {
      console.error("Failed to download attachment:", error);
    }
  };

  const disabled = isAnyMutating || isDuplicating;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-[90vw] sm:max-w-[90vw]">
        <DialogHeader>
          <DialogTitle>
            <div className="flex items-center justify-between gap-2">
              <span className="flex min-w-0 items-center gap-2">
                <span className="truncate">{current.name || "Attachment"}</span>
                {attachments.length > 1 && (
                  <span className="shrink-0 text-body font-normal text-foreground-secondary">
                    {currentIndex + 1} / {attachments.length}
                  </span>
                )}
              </span>
              <span className="flex shrink-0 items-center gap-2">
                {headerExtras}
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <DropdownSettingsButton className="shrink-0" />
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    {!hideEditAnnotations &&
                      photoActions.canEdit &&
                      !current.isVideo && (
                        <DropdownMenuItem
                          onClick={() => {
                            onOpenChange(false);
                            onEditAnnotations(current);
                          }}
                          disabled={disabled}
                          icon={<FiEdit2 className="h-4 w-4" />}
                        >
                          Edit Annotations
                        </DropdownMenuItem>
                      )}
                    {onDuplicate && photoActions.canDuplicate && (
                      <DropdownMenuItem
                        onClick={() => onDuplicate(current)}
                        disabled={disabled}
                        icon={<FiCopy className="h-4 w-4" />}
                      >
                        {isDuplicating ? "Duplicating..." : "Duplicate"}
                      </DropdownMenuItem>
                    )}
                    {onRename && photoActions.canRename && (
                      <DropdownMenuItem
                        onClick={() => onRename(current)}
                        disabled={disabled}
                        icon={<FiEdit2 className="h-4 w-4" />}
                      >
                        Rename
                      </DropdownMenuItem>
                    )}
                    {onCompare && attachments.length > 1 && (
                      <DropdownMenuItem
                        onClick={() => onCompare(current)}
                        disabled={disabled}
                        icon={<FiGitPullRequest className="h-4 w-4" />}
                      >
                        Compare
                      </DropdownMenuItem>
                    )}
                    {onSkinAnalysis &&
                      !current.isVideo &&
                      !current.isPlaceholder &&
                      isImageUrl(
                        current.gcsPath || current.url || current.name,
                      ) && (
                        <DropdownMenuItem
                          onClick={() => {
                            onOpenChange(false);
                            onSkinAnalysis(current);
                          }}
                          disabled={disabled}
                          icon={<FiZap className="h-4 w-4" />}
                        >
                          Skin Analysis
                        </DropdownMenuItem>
                      )}
                    {onSetAsProfilePhoto &&
                      !current.isVideo &&
                      !current.isPlaceholder &&
                      current.patientId &&
                      isImageUrl(
                        current.gcsPath || current.url || current.name,
                      ) && (
                        <DropdownMenuItem
                          onClick={() => onSetAsProfilePhoto(current)}
                          disabled={disabled}
                          icon={<FiUser className="h-4 w-4" />}
                        >
                          Set as Profile Photo
                        </DropdownMenuItem>
                      )}
                    {onShowAsTaken && (
                      <DropdownMenuItem
                        onClick={onShowAsTaken}
                        disabled={disabled}
                        icon={<FiImage className="h-4 w-4" />}
                      >
                        Show as taken
                      </DropdownMenuItem>
                    )}
                    {canDownload && (
                      <DownloadMenuItem
                        key={current.id}
                        photo={current.isVideo ? undefined : current}
                        onDownload={handleDownload}
                        disabled={disabled}
                      />
                    )}
                    {onLinkToNote && photoActions.canEdit && (
                      <DropdownMenuItem
                        onClick={() => onLinkToNote(current)}
                        disabled={disabled}
                        icon={<FiFileText className="h-4 w-4" />}
                      >
                        {current.noteId ? "Change Note" : "Link to Note"}
                      </DropdownMenuItem>
                    )}
                    {onChangePatient && photoActions.canEdit && (
                      <DropdownMenuItem
                        onClick={() => onChangePatient(current)}
                        disabled={disabled}
                        icon={<FiUsers className="h-4 w-4" />}
                      >
                        Change Patient
                      </DropdownMenuItem>
                    )}
                    {onUnlinkFromNote &&
                      photoActions.canEdit &&
                      current.noteId && (
                        <DropdownMenuItem
                          onClick={() => onUnlinkFromNote(current)}
                          disabled={disabled}
                          icon={<FiMinusCircle className="h-4 w-4" />}
                        >
                          Remove from Note
                        </DropdownMenuItem>
                      )}
                    {onRemove && photoActions.canEdit && (
                      <>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem
                          onClick={() => onRemove(current)}
                          disabled={disabled}
                          icon={<FiTrash2 className="h-4 w-4" />}
                          className="text-status-error-text"
                        >
                          Delete
                        </DropdownMenuItem>
                      </>
                    )}
                  </DropdownMenuContent>
                </DropdownMenu>
              </span>
            </div>
          </DialogTitle>
        </DialogHeader>
        <DialogBody className="relative p-0">
          {/* Navigation arrows */}
          {currentIndex > 0 && (
            <button
              onClick={goPrev}
              className="absolute left-2 top-1/2 z-10 -translate-y-1/2 rounded-full bg-black/50 p-2 text-white transition-colors hover:bg-black/70"
              aria-label="Previous image"
            >
              <FiChevronLeft className="h-6 w-6" />
            </button>
          )}
          {currentIndex < attachments.length - 1 && (
            <button
              onClick={goNext}
              className="absolute right-2 top-1/2 z-10 -translate-y-1/2 rounded-full bg-black/50 p-2 text-white transition-colors hover:bg-black/70"
              aria-label="Next image"
            >
              <FiChevronRight className="h-6 w-6" />
            </button>
          )}

          {/* Swipe support */}
          <div
            onTouchStart={(e) => {
              // Only track single-finger swipes (not pinch gestures)
              if (e.touches.length !== 1) {
                touchStartRef.current = null;
                return;
              }
              const touch = e.touches[0];
              touchStartRef.current = { x: touch.clientX, y: touch.clientY };
            }}
            onTouchEnd={(e) => {
              if (!touchStartRef.current) return;
              const touch = e.changedTouches[0];
              const dx = touch.clientX - touchStartRef.current.x;
              const dy = touch.clientY - touchStartRef.current.y;
              touchStartRef.current = null;
              // Only navigate if horizontal swipe is dominant and significant
              if (Math.abs(dx) > 50 && Math.abs(dx) > Math.abs(dy) * 2) {
                if (dx < 0) goNext();
                else goPrev();
              }
            }}
          >
            {current.isVideo ? (
              <div className="flex h-[50vh] items-center justify-center bg-black sm:h-[70vh]">
                <video
                  key={current.id}
                  src={current.url}
                  controls
                  playsInline
                  className="max-h-full max-w-full"
                />
              </div>
            ) : (
              <ReadOnlyAnnotator
                key={imageUrlOverride ? "studio" : "original"}
                imageUrl={imageUrlOverride || current.url}
                transparentBackdrop={!!imageUrlOverride && imageTransparent}
                gcsPath={current.gcsPath}
                annotations={current.annotations}
                onAnnotationsChange={() => {}}
                readOnly
                hideDownload
                className="h-[50vh] sm:h-[70vh]"
              />
            )}
          </div>
        </DialogBody>
      </DialogContent>
    </Dialog>
  );
};

const AttachmentDropdownMenu = ({
  show,
  isAnyAttachmentMutating,
  isDuplicating,
  onCompare,
  onRemove,
  onDuplicate,
  onRename,
  onLinkToNote,
  onUnlinkFromNote,
  onChangePatient,
  onEditAnnotations,
  onEditAnnotationsFullScreen,
  onDownload,
  downloadPhoto,
  onSkinAnalysis,
  onSetAsProfilePhoto,
  onShareToPortal,
  isSharedToPortal,
  attachmentId,
}: {
  show: boolean;
  isAnyAttachmentMutating: boolean;
  isDuplicating?: boolean;
  onCompare?: () => void;
  onRemove?: (attachmentId: string) => void;
  onDuplicate?: () => void;
  onRename?: () => void;
  onLinkToNote?: () => void;
  onUnlinkFromNote?: () => void;
  onChangePatient?: () => void;
  onEditAnnotations?: () => void;
  onEditAnnotationsFullScreen?: () => void;
  onDownload?: () => void;
  /** The photo Download shares to the iOS share sheet; omit for videos. */
  downloadPhoto?: { id: string; gcsPath?: string; name?: string };
  onSkinAnalysis?: () => void;
  onSetAsProfilePhoto?: () => void;
  onShareToPortal?: () => void;
  isSharedToPortal?: boolean;
  attachmentId: string;
}) => {
  const { currentUser } = useUser();
  const canDownloadFiles = hasPermission(
    currentUser,
    PERMISSIONS.CLINICAL_GALLERY_DOWNLOAD,
  );
  const { canEdit, canDuplicate, canRename } = galleryPhotoActions(currentUser);

  if (!show) return null;

  const disabled = isAnyAttachmentMutating || isDuplicating;

  return (
    <div
      className="absolute -right-1 -top-1"
      onClick={(e) => e.stopPropagation()}
    >
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <DropdownSettingsButton className="h-6 w-6 rounded-full bg-black/50 text-white hover:bg-black/70" />
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          {onEditAnnotations && canEdit && (
            <DropdownMenuItem
              onClick={onEditAnnotations}
              disabled={disabled}
              icon={<FiEdit2 className="h-4 w-4" />}
            >
              Edit Annotations
            </DropdownMenuItem>
          )}
          {onEditAnnotationsFullScreen && canEdit && (
            <DropdownMenuItem
              onClick={onEditAnnotationsFullScreen}
              disabled={disabled}
              icon={<FiExternalLink className="h-4 w-4" />}
            >
              Annotate Full Screen
            </DropdownMenuItem>
          )}
          {onDuplicate && canDuplicate && (
            <DropdownMenuItem
              onClick={onDuplicate}
              disabled={disabled}
              icon={<FiCopy className="h-4 w-4" />}
            >
              {isDuplicating ? "Duplicating..." : "Duplicate"}
            </DropdownMenuItem>
          )}
          {onRename && canRename && (
            <DropdownMenuItem
              onClick={onRename}
              disabled={disabled}
              icon={<FiEdit2 className="h-4 w-4" />}
            >
              Rename
            </DropdownMenuItem>
          )}
          {onCompare && (
            <DropdownMenuItem
              onClick={onCompare}
              disabled={disabled}
              icon={<FiGitPullRequest className="h-4 w-4" />}
            >
              Compare
            </DropdownMenuItem>
          )}
          {onSkinAnalysis && (
            <DropdownMenuItem
              onClick={onSkinAnalysis}
              disabled={disabled}
              icon={<FiZap className="h-4 w-4" />}
            >
              Skin Analysis
            </DropdownMenuItem>
          )}
          {onSetAsProfilePhoto && (
            <DropdownMenuItem
              onClick={onSetAsProfilePhoto}
              disabled={disabled}
              icon={<FiUser className="h-4 w-4" />}
            >
              Set as Profile Photo
            </DropdownMenuItem>
          )}
          {onShareToPortal && canEdit && (
            <DropdownMenuItem
              onClick={onShareToPortal}
              disabled={disabled}
              icon={<FiShare2 className="h-4 w-4" />}
            >
              {isSharedToPortal
                ? "Patient portal sharing"
                : "Share to patient portal"}
            </DropdownMenuItem>
          )}
          {onDownload && canDownloadFiles && (
            <DownloadMenuItem
              photo={downloadPhoto}
              onDownload={onDownload}
              disabled={disabled}
            />
          )}
          {onLinkToNote && canEdit && (
            <DropdownMenuItem
              onClick={onLinkToNote}
              disabled={disabled}
              icon={<FiFileText className="h-4 w-4" />}
            >
              Change Note
            </DropdownMenuItem>
          )}
          {onChangePatient && canEdit && (
            <DropdownMenuItem
              onClick={onChangePatient}
              disabled={disabled}
              icon={<FiUsers className="h-4 w-4" />}
            >
              Change Patient
            </DropdownMenuItem>
          )}
          {onUnlinkFromNote && canEdit && (
            <DropdownMenuItem
              onClick={onUnlinkFromNote}
              disabled={disabled}
              icon={<FiMinusCircle className="h-4 w-4" />}
            >
              Remove from Note
            </DropdownMenuItem>
          )}
          {onRemove && canEdit && (
            <>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                onClick={() => onRemove(attachmentId)}
                disabled={disabled}
                icon={<FiTrash2 className="h-4 w-4" />}
                className="text-status-error-text"
              >
                Delete
              </DropdownMenuItem>
            </>
          )}
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
};

const AttachmentTagsRow = ({
  resolved,
  disabled,
  allTags,
  onAddTag,
  onRemoveTag,
}: {
  resolved: ResolvedAttachment;
  disabled: boolean;
  allTags: NoteAttachmentTag[];
  onAddTag: (tagId: string) => void;
  onRemoveTag: (tagId: string) => void;
}) => {
  return (
    <div
      className="mb-1 flex flex-wrap items-center gap-1.5"
      onClick={(e) => e.stopPropagation()}
    >
      {resolved.tags.map((tag) => (
        <ImageTagBadge
          key={tag.id}
          tag={tag}
          onRemoveTag={!disabled ? onRemoveTag : undefined}
        />
      ))}
      {!disabled && (
        <ImageTagBadge
          isAddButton
          onAddTag={onAddTag}
          availableTags={allTags}
          assignedTagIds={resolved.tags.map((t) => t.id)}
        />
      )}
    </div>
  );
};

const AttachmentName = ({
  resolved,
  disabled,
  canEditName,
  editingNameId,
  editingName,
  savingNameId,
  setEditingName,
  handleEditName,
  handleSaveName,
  handleCancelEditName,
  attachment,
}: {
  resolved: ResolvedAttachment;
  disabled: boolean;
  canEditName: boolean;
  editingNameId?: string | null;
  editingName: string;
  savingNameId?: string | null;
  setEditingName?: (name: string) => void;
  handleEditName?: (attachment: NoteAttachment) => void;
  handleSaveName?: (attachmentId: string) => void;
  handleCancelEditName?: () => void;
  attachment: NoteAttachment;
}) => {
  const isEditing = editingNameId === resolved.id && canEditName;

  if (isEditing) {
    return (
      <div className="flex items-center gap-2">
        <Input
          value={editingName}
          onChange={(e) => setEditingName?.(e.target.value)}
          className="flex-1 !rounded !border-0 !bg-background/90 px-2 py-1 text-caption-strong text-foreground placeholder-muted-foreground outline-none backdrop-blur-sm focus:!ring-2 focus:!ring-border/50"
          placeholder="Enter name..."
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              handleSaveName?.(resolved.id);
            } else if (e.key === "Escape") {
              e.preventDefault();
              handleCancelEditName?.();
            }
          }}
          onBlur={() => handleSaveName?.(resolved.id)}
          autoFocus
          disabled={savingNameId === resolved.id}
        />
        {savingNameId === resolved.id && (
          <LuLoader2 className="h-3 w-3 animate-spin text-white" />
        )}
      </div>
    );
  }

  return (
    <div
      className={cx(
        "group/name flex cursor-pointer items-center justify-between transition-all duration-200",
        !disabled &&
          canEditName &&
          "-mx-1 -my-0.5 rounded px-1 py-0.5 hover:bg-background/10",
      )}
      onClick={() =>
        !disabled &&
        canEditName &&
        handleEditName?.(attachment as NoteAttachment)
      }
    >
      <span
        className="truncate pr-1 text-caption-strong text-white"
        title={resolved.name || "Click to add name"}
      >
        {resolved.name || "Untitled"}
      </span>
      {!disabled && canEditName && (
        <FiEdit2 className="h-3 w-3 flex-shrink-0 text-white/70 opacity-0 transition-opacity duration-200 group-hover/name:opacity-100" />
      )}
    </div>
  );
};

export const AttachmentItemFooterContainer = ({
  children,
}: {
  children: ReactNode;
}) => {
  return (
    <div className="absolute bottom-0 left-0 right-0 rounded-b-lg bg-gradient-to-t from-black/80 via-black/40 to-transparent p-2">
      {children}
    </div>
  );
};

export const AttachmentItemDefaultFooterActions = ({
  resolved,
  tenant,
  showPatientLink = true,
  showNoteLink = true,
}: {
  resolved: ResolvedAttachment;
  tenant: string;
  showPatientLink?: boolean;
  showNoteLink?: boolean;
}) => {
  const patientLinkId = resolved.patientId;
  const noteLinkId = resolved.noteId ?? undefined;
  const patientLabel =
    resolved.patient?.firstName || resolved.patient?.lastName
      ? formatName(resolved.patient.firstName, resolved.patient.lastName)
      : "View Patient";

  return (
    <>
      {showNoteLink && noteLinkId && (
        <Link
          href={`/${tenant}/scribe/playground/${noteLinkId}`}
          target="_blank"
          rel="noopener noreferrer"
          onClick={(e) => e.stopPropagation()}
          className="mb-0.5 flex items-center gap-1 truncate text-tiny font-medium text-link hover:text-link-hover hover:underline"
        >
          <FiExternalLink className="h-3 w-3" />
          View Note
        </Link>
      )}

      {showPatientLink && patientLinkId && (
        <Link
          href={`/${tenant}/patient/${patientLinkId}`}
          target="_blank"
          rel="noopener noreferrer"
          onClick={(e) => e.stopPropagation()}
          className="mb-0.5 flex items-center gap-1 truncate text-tiny font-medium text-foreground-secondary hover:text-foreground hover:underline"
        >
          <FiExternalLink className="h-3 w-3" />
          {patientLabel}
        </Link>
      )}
    </>
  );
};

const AttachmentFooterOverlay = ({
  resolved,
  tenant,
  disabled,
  isOptimisticEntry,
  tagControls,
  onAddTag,
  onRemoveTag,
  actions,
  canEditName,
  nameEditing,
  attachment,
}: {
  resolved: ResolvedAttachment;
  tenant: string;
  disabled: boolean;
  isOptimisticEntry: boolean;
  tagControls?: AttachmentItemProps["tagControls"];
  onAddTag: (tagId: string) => void;
  onRemoveTag: (tagId: string) => void;
  actions?:
    | ReactNode
    | ((args: { resolved: ResolvedAttachment; tenant: string }) => ReactNode);
  canEditName: boolean;
  nameEditing: AttachmentItemProps["nameEditing"];
  attachment: NoteAttachment;
}) => {
  return (
    <AttachmentItemFooterContainer>
      {!isOptimisticEntry && (
        <AttachmentTagsRow
          resolved={resolved}
          disabled={disabled}
          allTags={tagControls?.allTags ?? []}
          onAddTag={onAddTag}
          onRemoveTag={onRemoveTag}
        />
      )}

      {typeof actions === "function"
        ? actions({ resolved, tenant })
        : (actions ?? (
            <AttachmentItemDefaultFooterActions
              resolved={resolved}
              tenant={tenant}
            />
          ))}

      <AttachmentName
        resolved={resolved}
        disabled={disabled}
        canEditName={canEditName}
        editingNameId={nameEditing?.editingNameId}
        editingName={nameEditing?.editingName ?? ""}
        savingNameId={nameEditing?.savingNameId}
        setEditingName={nameEditing?.setEditingName}
        handleEditName={nameEditing?.handleEditName}
        handleSaveName={nameEditing?.handleSaveName}
        handleCancelEditName={nameEditing?.handleCancelEditName}
        attachment={attachment}
      />
    </AttachmentItemFooterContainer>
  );
};

interface AttachmentItemProps {
  attachment: NoteAttachment;
  ui?: {
    disabled?: boolean;
    isUploading?: boolean;
    isAnyAttachmentMutating?: boolean;
  };
  tagControls?: {
    allTags: NoteAttachmentTag[];
    onAssignTags: (attachmentId: string, tagIds: string[]) => void;
  };
  nameEditing?: {
    editingNameId?: string | null;
    editingName?: string;
    setEditingName?: (name: string) => void;
    savingNameId?: string | null;
    handleEditName?: (attachment: NoteAttachment) => void;
    handleSaveName?: (attachmentId: string) => void;
    handleCancelEditName?: () => void;
  };
  annotationControls?: {
    setAnnotatingAttachment?: (attachment: NoteAttachment) => void;
    setCurrentAnnotations?: (annotations: Annotation[]) => void;
    setAnnotationModalOpen?: (open: boolean) => void;
  };
  placeholderControls?: {
    isPlaceholder?: boolean;
    convertingPlaceholderId?: string | null;
    setConvertingPlaceholderId?: (id: string | null) => void;
    convertPlaceholderToRealAttachment?: (
      attachment: NoteAttachment,
    ) => Promise<NoteAttachment | null>;
  };
  uploadControls?: {
    handleFileChange?: (
      files: FileList | null,
      targetAttachment?: NoteAttachment,
      customName?: string,
    ) => void;
    onCameraOpen?: (targetAttachment: NoteAttachment) => void;
  };
  onRemove?: (attachmentId: string) => void;
  onCompare?: () => void;
  onDuplicate?: () => void;
  onRename?: () => void;
  onLinkToNote?: () => void;
  onChangePatient?: () => void;
  isDuplicating?: boolean;
  shouldShowThumbnail?: boolean;
  noteId?: string | null;
  patientId?: string;
  // Gallery support
  onOpenGallery?: (attachmentIndex: number) => void;
  attachmentIndex?: number;
}

type AttachmentItemController = {
  resolved: ResolvedAttachment;
  disabled: boolean;
  isUploading: boolean;
  isAnyAttachmentMutating: boolean;
  isPlaceholder: boolean;
  isOptimisticEntry: boolean;
  hasValidImageUrl: boolean;
  converting: boolean;
  canAnnotate: boolean;
  canEditName: boolean;
  canRemove: boolean;
  isDuplicating: boolean;
  containerClassName: string;
  isPreviewOpen: boolean;
  setIsPreviewOpen: (open: boolean) => void;
  onContainerClick: () => Promise<void>;
  onPlaceholderCameraOpen?: () => void;
  handleAddTag: (tagId: string) => Promise<void>;
  handleRemoveTag: (tagId: string) => void;
};

export const useAttachmentItemController = ({
  attachment,
  ui,
  tagControls,
  nameEditing,
  annotationControls,
  placeholderControls,
  uploadControls,
  shouldShowThumbnail = false,
  onRemove,
  onCompare: _onCompare,
  isDuplicating: isDuplicatingProp = false,
  noteId,
  patientId,
  onOpenGallery,
  attachmentIndex,
}: AttachmentItemProps): AttachmentItemController => {
  const {
    disabled = false,
    isUploading = false,
    isAnyAttachmentMutating = false,
  } = ui ?? {};
  const { handleEditName } = nameEditing ?? {};
  const { setAnnotatingAttachment, setAnnotationModalOpen } =
    annotationControls ?? {};
  const { isPlaceholder = false, convertingPlaceholderId } =
    placeholderControls ?? {};
  const { handleFileChange, onCameraOpen } = uploadControls ?? {};

  const resolved = resolveAttachment({
    attachment,
    shouldShowThumbnail,
    noteId,
    patientId,
  });

  const handleAddTag = async (tagId: string) => {
    if (!resolved.id || resolved.id.startsWith("temp-")) return;
    if (!tagControls?.onAssignTags) return;

    const currentTagIds = resolved.tags.map((t) => t.id);
    if (!currentTagIds.includes(tagId)) {
      tagControls.onAssignTags(resolved.id, [...currentTagIds, tagId]);
    }
  };

  const handleRemoveTag = (tagId: string) => {
    if (!resolved.id || resolved.id.startsWith("temp-")) return;
    if (!tagControls?.onAssignTags) return;

    const updatedTagIds = resolved.tags
      .filter((t) => t.id !== tagId)
      .map((t) => t.id);

    tagControls.onAssignTags(resolved.id, updatedTagIds);
  };

  // Separate capability checks for independent features
  const canAnnotate = !!(setAnnotatingAttachment && setAnnotationModalOpen);
  const canEditName = !!handleEditName;
  const canRemove = !!onRemove;

  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  // Check if this is an optimistic entry (still uploading)
  const isOptimisticEntry = resolved.id?.startsWith("temp-") ?? false;
  // Check if URL is valid for display (http or blob)
  const hasValidImageUrl =
    resolved.url?.startsWith("http") || resolved.url?.startsWith("blob:");
  const converting = convertingPlaceholderId === resolved.id;
  const placeholderUploadInputRef = useRef<HTMLInputElement | null>(null);
  const cleanupPlaceholderUploadInput = useCallback(() => {
    const input = placeholderUploadInputRef.current;
    if (!input) return;

    input.onchange = null;
    input.remove();
    placeholderUploadInputRef.current = null;
  }, []);

  useEffect(() => {
    return cleanupPlaceholderUploadInput;
  }, [cleanupPlaceholderUploadInput]);

  const containerClassName = cx(
    "relative aspect-square overflow-hidden rounded-lg border bg-background-secondary",
    // Optimistic entries can't be opened until the server has them
    isOptimisticEntry &&
      (isLocalFileUrl(resolved.url) ? "cursor-wait" : "cursor-progress"),
    // Placeholder without image - show pointer for upload
    !isOptimisticEntry &&
      isPlaceholder &&
      !hasValidImageUrl &&
      "cursor-pointer border-dashed hover:bg-hover",
    // Attachments with valid URL (including template placeholders with images) - show zoom cursor
    !isOptimisticEntry && hasValidImageUrl && "cursor-zoom-in",
  );

  const onContainerClick = async () => {
    // Don't allow any interaction with optimistic entries
    if (isOptimisticEntry) return;

    if (
      handleFileChange &&
      isPlaceholder &&
      !hasValidImageUrl &&
      !disabled &&
      !isUploading &&
      !isAnyAttachmentMutating
    ) {
      cleanupPlaceholderUploadInput();
      document
        .querySelectorAll<HTMLInputElement>(
          'input[data-placeholder-upload-input="true"]',
        )
        .forEach((el) => el.remove());

      const input = document.createElement("input");
      input.type = "file";
      input.accept = "image/*,video/*";
      input.style.display = "none";
      input.dataset.placeholderUploadInput = "true";

      const cleanup = () => {
        input.onchange = null;
        input.remove();
        if (placeholderUploadInputRef.current === input) {
          placeholderUploadInputRef.current = null;
        }
      };

      input.onchange = (e) => {
        const target = e.target as HTMLInputElement;
        handleFileChange(target.files, attachment);
        cleanup();
      };
      input.addEventListener("cancel", cleanup, { once: true });

      // Keep the input attached while the native picker/camera sheet is open.
      // Mobile Safari can drop change events for detached, one-off inputs,
      // which makes later placeholder captures look like they did nothing.
      placeholderUploadInputRef.current = input;
      document.body.appendChild(input);
      input.click();
      return;
    }

    if (!resolved.url.startsWith("http")) return;

    // Open gallery lightbox if available (grid/gallery views)
    if (onOpenGallery && attachmentIndex !== undefined) {
      onOpenGallery(attachmentIndex);
      return;
    }

    // If annotations are available, open annotation editor directly (playground)
    // Videos don't support annotation
    if (
      canAnnotate &&
      !resolved.isVideo &&
      !disabled &&
      !isAnyAttachmentMutating
    ) {
      setAnnotatingAttachment?.(attachment);
      setAnnotationModalOpen?.(true);
      return;
    }

    // Fallback: show preview dialog
    setIsPreviewOpen(true);
  };

  const canOpenPlaceholderCamera =
    !!onCameraOpen &&
    isPlaceholder &&
    !hasValidImageUrl &&
    !disabled &&
    !isUploading &&
    !isAnyAttachmentMutating &&
    !isOptimisticEntry;

  const onPlaceholderCameraOpen = canOpenPlaceholderCamera
    ? () => onCameraOpen?.(attachment)
    : undefined;

  return {
    resolved,
    disabled,
    isUploading,
    isAnyAttachmentMutating,
    isPlaceholder,
    isOptimisticEntry,
    hasValidImageUrl,
    converting,
    canAnnotate,
    canEditName,
    canRemove,
    isDuplicating: isDuplicatingProp,
    containerClassName,
    isPreviewOpen,
    setIsPreviewOpen,
    onContainerClick,
    onPlaceholderCameraOpen,
    handleAddTag,
    handleRemoveTag,
  };
};

export const AttachmentItem = ({
  attachment,
  ui,
  tagControls,
  nameEditing,
  annotationControls,
  placeholderControls,
  uploadControls,
  shouldShowThumbnail = false,
  onRemove,
  onCompare,
  onDuplicate,
  onRename,
  onLinkToNote,
  onChangePatient,
  isDuplicating,
  noteId,
  patientId,
  onOpenGallery,
  attachmentIndex,
}: AttachmentItemProps) => {
  const {
    editingNameId,
    editingName = "",
    setEditingName,
    savingNameId,
    handleEditName,
    handleSaveName,
    handleCancelEditName,
  } = nameEditing ?? {};
  const { tenant } = useParams<{ tenant: string }>();

  const controller = useAttachmentItemController({
    attachment,
    ui,
    tagControls,
    nameEditing,
    annotationControls,
    placeholderControls,
    uploadControls,
    shouldShowThumbnail,
    onRemove,
    onCompare,
    onDuplicate,
    onRename,
    isDuplicating,
    noteId,
    patientId,
    onOpenGallery,
    attachmentIndex,
  });

  return (
    <div className="group relative">
      <div
        className={controller.containerClassName}
        onClick={() => void controller.onContainerClick()}
      >
        <AttachmentMedia
          resolved={controller.resolved}
          attachment={attachment}
          isUploading={controller.isUploading}
          converting={controller.converting}
          isOptimisticEntry={controller.isOptimisticEntry}
          hasValidImageUrl={controller.hasValidImageUrl}
          onCameraOpen={controller.onPlaceholderCameraOpen}
        />
      </div>

      <AttachmentDropdownMenu
        show={
          !controller.disabled &&
          !controller.isOptimisticEntry &&
          !!controller.resolved.id &&
          controller.canRemove
        }
        isAnyAttachmentMutating={controller.isAnyAttachmentMutating}
        isDuplicating={controller.isDuplicating}
        onCompare={onCompare}
        onRemove={onRemove}
        onDuplicate={onDuplicate}
        onRename={onRename}
        onLinkToNote={onLinkToNote}
        onChangePatient={onChangePatient}
        attachmentId={controller.resolved.id}
      />

      <AttachmentFooterOverlay
        resolved={controller.resolved}
        tenant={tenant}
        disabled={controller.disabled}
        isOptimisticEntry={controller.isOptimisticEntry}
        tagControls={tagControls}
        onAddTag={controller.handleAddTag}
        onRemoveTag={controller.handleRemoveTag}
        actions={undefined}
        canEditName={controller.canEditName}
        nameEditing={{
          editingNameId,
          editingName,
          setEditingName,
          savingNameId,
          handleEditName,
          handleSaveName,
          handleCancelEditName,
        }}
        attachment={attachment}
      />
    </div>
  );
};

// Export the building blocks for composition in higher-level components (e.g. AttachmentsGrid)
export {
  AttachmentDropdownMenu as AttachmentItemActionButtons,
  AttachmentFooterOverlay as AttachmentItemFooterOverlay,
  AttachmentGalleryDialog,
  AttachmentMedia as AttachmentItemMedia,
  LazyImage,
  resolveAttachment,
};
export type { ResolvedAttachment };
